import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /** "subtle" = card discreto pra dentro de seções; "hero" = tela inteira com gradient blob */
  variant?: 'subtle' | 'hero';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  variant = 'subtle',
}: EmptyStateProps) {
  if (variant === 'hero') {
    return (
      <div
        className={cn(
          'octa-fade-in-up border-border/40 bg-card/60 shadow-octa-card relative isolate flex flex-col items-center justify-center gap-5 overflow-hidden rounded-3xl border px-6 py-16 text-center backdrop-blur-sm',
          className,
        )}
      >
        {/* blob decorativo */}
        <div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -top-16 left-1/2 -z-10 h-48 w-48 -translate-x-1/2 opacity-30"
        />
        {Icon && (
          <div className="relative">
            <div
              aria-hidden
              className="bg-octa-gradient absolute inset-0 -z-10 rounded-3xl opacity-25 blur-2xl"
            />
            <div className="bg-octa-gradient shadow-octa-lg shadow-octa-glow flex h-20 w-20 items-center justify-center rounded-3xl text-white">
              <Icon className="h-9 w-9" />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <h3 className="text-display-sm font-extrabold tracking-tight">{title}</h3>
          {description && (
            <p className="text-muted-foreground mx-auto max-w-sm text-sm leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {action && <div className="pt-1">{action}</div>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'octa-fade-in-up border-border/60 bg-card/40 flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed px-6 py-12 text-center',
        className,
      )}
    >
      {Icon && (
        <div className="relative">
          <div aria-hidden className="bg-primary/15 absolute inset-0 -z-10 rounded-2xl blur-lg" />
          <div className="from-primary/10 to-primary/5 ring-primary/15 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ring-1">
            <Icon className="text-primary h-6 w-6" />
          </div>
        </div>
      )}
      <div className="space-y-1">
        <h3 className="text-base font-bold tracking-tight">{title}</h3>
        {description && (
          <p className="text-muted-foreground mx-auto max-w-sm text-sm">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}
